import { useState, FormEvent } from 'react';
import { Link } from 'react-router-dom';
import {
  Button,
  Input,
  Label,
  Card,
  CardHeader,
  CardTitle,
  CardContent,
  CardDescription,
} from '@lognai/synapse-design-system-v2';
import { useAuth } from '../../hooks/useAuth';

export default function ForgotPasswordPage() {
  const { forgotPassword, isForgotPasswordPending } = useAuth();

  const [email, setEmail] = useState('');
  const [sent, setSent] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);

    const result = await forgotPassword({ email });

    if (result.success) {
      setSent(true);
    } else {
      setError(result.error || 'Something went wrong. Please try again.');
    }
  };

  return (
    <Card className="w-full max-w-md">
      <CardHeader className="flex flex-col items-center space-y-4 text-center pb-6">
        <div className="text-4xl font-bold text-primary">LogNAI</div>
        <CardTitle className="text-xl font-normal">Forgot your password?</CardTitle>
        <CardDescription className="text-sm text-muted-foreground max-w-xs">
          {sent
            ? 'Check your inbox for a reset link.'
            : "Enter your email and we'll send you a link to reset your password."}
        </CardDescription>
      </CardHeader>

      <CardContent>
        {sent ? (
          <div className="space-y-4 text-center">
            <div className="rounded-lg bg-green-50 p-4 text-sm text-green-700 dark:bg-green-900/20 dark:text-green-400">
              A password reset link has been sent to <strong>{email}</strong>. It expires in 1 hour.
            </div>
            <Link
              to="/login"
              className="block text-sm text-primary underline-offset-4 hover:underline"
            >
              Back to Sign In
            </Link>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="space-y-2">
              <Label htmlFor="forgot-email">Email address</Label>
              <Input
                id="forgot-email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@example.com"
                required
                autoFocus
              />
            </div>

            {error && (
              <div className="rounded-lg bg-destructive/10 p-3 text-sm text-destructive">
                {error}
              </div>
            )}

            <Button type="submit" className="w-full" disabled={isForgotPasswordPending}>
              {isForgotPasswordPending ? 'Sending...' : 'Send Reset Link'}
            </Button>

            <div className="text-center text-sm">
              <Link
                to="/login"
                className="text-muted-foreground underline-offset-4 hover:text-primary hover:underline"
              >
                Back to Sign In
              </Link>
            </div>
          </form>
        )}
      </CardContent>
    </Card>
  );
}
